import { css } from '@emotion/react';
import styled from '@emotion/styled';
import isString from 'lodash/isString';
import React, { ButtonHTMLAttributes, CSSProperties, MouseEvent, ReactNode } from 'react';
import { EVENTS, sendEvent } from '../../../utils/analytics/analytics';
import { Link } from '../../../utils/i18n';
import { colors, spacing } from '../../../utils/styleguide';
import Icon, { IconNames } from '../Icon';
import Spinner from '../Spinner';
import Typography from '../text/Typography';

const SpinnerContainer = styled.div`
  position: absolute;
  top: ${spacing[2]}px;
  bottom: ${spacing[2]}px;
  right: ${spacing[4]}px;
  left: ${spacing[4]}px;
  display: flex;
  justify-content: center;
`;

const variantStyles = {
  onYellow: {
    solid: css`
      border: 2px solid ${colors.black};
      background-color: ${colors.black};

      :hover {
        background-color: #2c2e3a;
      }

      :focus {
        box-shadow: 0 0 0 2px ${colors.yellow}, 0 0 0 3px ${colors.black};
        outline: none;
      }

      :disabled {
        background-color: ${colors.lightGrey};
        border-color: ${colors.grey};
        color: ${colors.grey};
      }
    `,
    outline: css`
      background-color: ${colors.yellow};
      border: 2px solid ${colors.black};

      :hover {
        background-color: #f5da00;
      }

      :focus {
        box-shadow: 0 0 0 2px ${colors.yellow}, 0 0 0 3px ${colors.black};
        outline: none;
      }

      :disabled {
        background-color: ${colors.lightGrey};
        border-color: ${colors.grey};
        color: ${colors.grey};
      }
    `,
  },
  onLight: {
    solid: css`
      border: 2px solid ${colors.yellow};
      background-color: ${colors.yellow};

      :hover {
        background-color: #f5da00;
        border-color: #f5da00;
      }

      :focus {
        box-shadow: 0 0 0 2px ${colors.white}, 0 0 0 3px ${colors.black};
        outline: none;
      }

      :disabled {
        background-color: ${colors.lightGrey};
        border-color: ${colors.grey};
        color: ${colors.grey};
      }
    `,
    outline: css`
      border: 2px solid ${colors.yellow};
      background-color: ${colors.white};

      :hover {
        border-color: #f5da00;
        background-color: ${colors.brightGrey};
      }

      :focus {
        box-shadow: 0 0 0 2px ${colors.white}, 0 0 0 3px ${colors.black};
        outline: none;
      }

      :disabled {
        background-color: ${colors.lightGrey};
        border-color: ${colors.grey};
        color: ${colors.grey};
      }
    `,
  },
  onWhite: {
    // Deprecated in favor of onLight: Remove when no longer used in CMS
    solid: css`
      border: 2px solid ${colors.yellow};
      background-color: ${colors.yellow};

      :hover {
        background-color: #f5da00;
        border-color: #f5da00;
      }

      :focus {
        box-shadow: 0 0 0 2px ${colors.white}, 0 0 0 3px ${colors.black};
        outline: none;
      }

      :disabled {
        background-color: ${colors.lightGrey};
        border-color: ${colors.grey};
        color: ${colors.grey};
      }
    `,
    outline: css`
      border: 2px solid ${colors.yellow};
      background-color: ${colors.white};

      :hover {
        border-color: #f5da00;
        background-color: ${colors.brightGrey};
      }

      :focus {
        box-shadow: 0 0 0 2px ${colors.white}, 0 0 0 3px ${colors.black};
        outline: none;
      }

      :disabled {
        background-color: ${colors.lightGrey};
        border-color: ${colors.grey};
        color: ${colors.grey};
      }
    `,
  },
  onDark: {
    solid: css`
      border: 2px solid ${colors.yellow};
      background-color: ${colors.yellow};

      :hover {
        background-color: #f5da00;
        border-color: #f5da00;
      }

      :focus {
        box-shadow: 0 0 0 2px ${colors.black}, 0 0 0 3px ${colors.yellow};
        outline: none;
      }

      :disabled {
        background-color: ${colors.lightGrey};
        border-color: ${colors.grey};
        color: ${colors.grey};
      }
    `,
    outline: css`
      background-color: ${colors.black};
      border: 2px solid ${colors.yellow};

      :hover {
        background-color: #2c2e3a;
      }

      :focus {
        box-shadow: 0 0 0 2px ${colors.black}, 0 0 0 3px ${colors.yellow};
        outline: none;
      }

      :disabled {
        background-color: ${colors.lightGrey};
        border-color: ${colors.grey};
        color: ${colors.grey};
      }
    `,
  },
};

const variantTextColors = {
  onYellow: {
    solid: colors.white,
    outline: colors.black,
  },
  onLight: {
    // Deprecated: Remove when no longer used in CMS
    solid: colors.black,
    outline: colors.black,
  },
  onWhite: {
    solid: colors.black,
    outline: colors.black,
  },
  onDark: {
    solid: colors.black,
    outline: colors.yellow,
  },
};

export type ButtonColor = keyof typeof variantStyles;

type TextColor = keyof typeof variantTextColors;

export type ButtonVariant = 'solid' | 'outline';

const getTextColor = (variant: ButtonVariant, color: TextColor) => {
  const variantData = variantTextColors[color] || {};
  return variantData[variant];
};

const getStyle = (variant: ButtonVariant, color: ButtonColor) => {
  const variantData = variantStyles[color] || {};
  return variantData[variant] ? variantData[variant].styles : null;
};

type CommonProps = {
  label?: ReactNode;
  children?: ReactNode;
  background?: string;
  className?: string;
  variant?: ButtonVariant;
  color?: ButtonColor;
  showSpinner?: boolean;
  disabled?: boolean;
  size?: 'small' | 'large';
  leadingIconName?: IconNames;
  trailingIconName?: IconNames;
  href?: string | null;
  style?: CSSProperties;
};

type HTMLProps = Omit<ButtonHTMLAttributes<HTMLElement>, 'value' | 'onClick'>;

type ButtonClickProps = {
  onClick?: (event: MouseEvent) => void;
  tag?: string;
};

type LinkButtonProps = {
  href?: string;
  externalLink?: boolean;
};

export type ButtonProps = (ButtonClickProps | LinkButtonProps) & HTMLProps & CommonProps;

const PrimaryButton = ({
  label,
  color = 'onYellow',
  variant = 'solid',
  disabled,
  className,
  showSpinner,
  size = 'large',
  children,
  leadingIconName,
  trailingIconName,
  ...commonProps
}: ButtonProps) => {
  const { onClick, tag, ...linkProps } = commonProps as ButtonClickProps & HTMLProps;
  const { href, externalLink, ...rest } = linkProps as LinkButtonProps & HTMLProps;

  const isLink = href && !disabled;

  const textColor = getTextColor(variant, color);
  const onClickWithAnalytics = (event: MouseEvent) => {
    sendEvent(EVENTS.buttonClick.name, {
      name: isString(label) ? label : undefined,
      'from path': window.location.pathname,
      'from query': window.location.search,
      to: href || '',
    });
    return onClick?.(event);
  };
  const button = (
    <Typography
      useBuiltInMargins
      css={css`
        display: inline-flex;
        justify-content: center;
        align-items: center;
        box-sizing: border-box;
        position: relative;
        border-radius: 100px;
        border: none;
        -webkit-appearance: none;
        ${size === 'large'
          ? `padding: 15px ${spacing[4]}px 13px;`
          : `padding: 9px ${spacing[4]}px 7px;`}
        ${getStyle(variant, color)}
      `}
      className={className}
      color={showSpinner ? 'transparent' : textColor}
      variant={size === 'large' ? 'buttonLarge' : 'buttonSmall'}
      tag={isLink ? 'a' : tag}
      onClick={onClickWithAnalytics}
      disabled={showSpinner || disabled}
      href={href}
      {...rest}
    >
      {leadingIconName && (
        <Icon
          name={leadingIconName}
          size={16}
          stroke={textColor}
          css={css`
            margin-right: ${spacing[0]}px;
          `}
        />
      )}
      {label || children}
      {showSpinner && (
        <SpinnerContainer>
          <Spinner color={textColor} size={20} />
        </SpinnerContainer>
      )}
      {trailingIconName && (
        <Icon
          name={trailingIconName}
          size={16}
          stroke={textColor}
          css={css`
            margin-left: ${spacing[0]}px;
          `}
        />
      )}
    </Typography>
  );

  if (isLink && !externalLink && !href?.startsWith('#') && !href?.startsWith('http')) {
    return (
      <Link href={href as string} passHref>
        {button}
      </Link>
    );
  }

  return button;
};

export default PrimaryButton;
